import { createRouter, createWebHistory } from 'vue-router';
// 状态页是兜底页:静态引入,分包加载失败时它自己也要能显示
import StatusPage from './pages/Status.vue';
import { useSession } from './stores/session.js';
import { surfaceOf, useTenantStatus } from './stores/tenant.js';
import { useViewport } from './stores/viewport.js';
import { TENANT_WAIT_MS } from './ui/brand-identity.js';
import { desktopOnlyWhatOf } from './ui/h5.js';
import { roleNeedOf } from './ui/route-need.js';

/** 路由:学生线(成长地图 / 学习地图 / 关卡 / 结算 / 补强 / 测评 / 成长中心 / 题库)、家长端;meta.public 免登录,meta.student 需要当前学员 */
export const router = createRouter({
  history: createWebHistory(),
  // 换页面回到顶上(以前不设,点导航进新页面还停在上一页滚到的位置);浏览器前进 / 后退回到原来的位置;
  // 带 #锚点的滚到锚点;同一页只改了参数(页签、筛选这类 ?tab=)不动
  scrollBehavior(to, from, saved) {
    if (saved) return saved;
    if (to.hash) return { el: to.hash, top: 80 };
    if (to.path === from.path) return false;
    return { top: 0 };
  },
  routes: [
    // 平台官网(设计图 01–04,公开);登录后的入口分流在 /home
    {
      path: '/',
      name: 'site-home',
      component: () => import('./pages/site/SiteHome.vue'),
      meta: { public: true },
    },
    {
      path: '/site/courses',
      name: 'site-courses',
      component: () => import('./pages/site/SiteCourses.vue'),
      meta: { public: true },
    },
    {
      path: '/site/exams',
      name: 'site-exams',
      component: () => import('./pages/site/SiteExams.vue'),
      meta: { public: true },
    },
    // 证书核验(公开):平台认证证书二维码落这里;赛事证书编号也能查
    {
      path: '/verify',
      name: 'site-verify',
      component: () => import('./pages/site/SiteVerify.vue'),
      meta: { public: true },
    },
    {
      path: '/site/contests',
      name: 'site-contests',
      component: () => import('./pages/site/SiteContests.vue'),
      meta: { public: true },
    },
    {
      path: '/site/boards',
      name: 'site-boards',
      component: () => import('./pages/site/SiteBoards.vue'),
      meta: { public: true },
    },
    {
      path: '/site/about',
      name: 'site-about',
      component: () => import('./pages/site/SiteAbout.vue'),
      meta: { public: true },
    },
    // 加盟合作(2026-11-05 客户结构):合作价值 + 留资表单(平台后台「合作咨询」跟进)
    {
      path: '/site/partner',
      name: 'site-partner',
      component: () => import('./pages/site/SitePartner.vue'),
      meta: { public: true },
    },
    // 通用页(设计图 23):协议与政策
    {
      path: '/legal/:doc?',
      name: 'site-legal',
      component: () => import('./pages/site/SiteLegal.vue'),
      meta: { public: true, h5: true },
    },
    // meta.h5(学生端 H5 · 二期):true = 手机上出手机版式;'pc' = 只能在电脑上用,手机来了去「请用电脑」说明页;
    // 没标的(后台、大赛官网等)不归学生端 H5 管,手机上照旧是固定宽度的电脑版式。判定在 ui/viewport.ts。
    // 平台官网 / 机构站点整站和状态页是另一条线:它们本身就是自适应的(画板 fluid,见 ui/board.ts),和 meta.h5 无关
    { path: '/home', name: 'home', component: () => import('./pages/Home.vue'), meta: { h5: true } },
    {
      path: '/login',
      name: 'login',
      component: () => import('./pages/Login.vue'),
      meta: { public: true, h5: true },
    },
    {
      path: '/select-student',
      name: 'select-student',
      component: () => import('./pages/SelectStudent.vue'),
      meta: { h5: true },
    },
    {
      path: '/journey',
      name: 'journey',
      component: () => import('./pages/Journey.vue'),
      meta: { student: true, h5: true },
    },
    {
      path: '/courses/:courseId/map',
      name: 'map',
      component: () => import('./pages/LevelMap.vue'),
      meta: { student: true, h5: true },
    },
    {
      path: '/levels/:id',
      name: 'level',
      component: () => import('./pages/Level.vue'),
      meta: { student: true, h5: true },
    },
    {
      path: '/levels/:id/result',
      name: 'level-result',
      component: () => import('./pages/LevelResult.vue'),
      meta: { student: true, h5: true },
    },
    {
      path: '/levels/:id/remedy',
      name: 'remedy',
      component: () => import('./pages/Remedy.vue'),
      meta: { student: true, h5: true },
    },
    // ScratchJr 体系 · 低龄平板端(二期 · 原型 student/scratchjr):平板版的学习地图与关卡页。
    // 触屏优先、横屏为主,自己管 body.m-free 与样式,不挂学生端 H5 的手机版式;电脑上用鼠标也能完整操作。
    // 普通的 /courses/:id/map 与 /levels/:id 遇到低龄图标积木课会自己转到这里(带 ?std=1 就留在普通页)
    // 官网课程中心的「试玩图标积木」:三个小任务,不登录、只在本机跑、不留记录
    {
      path: '/jr/demo',
      name: 'jr-demo',
      component: () => import('./pages/jr/JrDemo.vue'),
      meta: { public: true },
    },
    {
      path: '/jr/courses/:courseId',
      name: 'jr-map',
      component: () => import('./pages/jr/JrMap.vue'),
      meta: { student: true },
    },
    {
      path: '/jr/levels/:id',
      name: 'jr-level',
      component: () => import('./pages/jr/JrLevel.vue'),
      meta: { student: true },
    },
    // 图形化工作室关卡(11-03 样张):左边回合卡 + 右边嵌入的 Scratch 编辑器(/scratch/cg.html,同源);
    // 编辑器要 1024 宽,窄屏只给「请用电脑」(页面自己处理)。普通的 /levels/:id 遇到工作室关会自己转到这里
    {
      path: '/studio/levels/:id',
      name: 'studio-level',
      component: () => import('./pages/studio/StudioLevel.vue'),
      meta: { student: true },
    },
    // 我的题库 · 编程练习的一道题(11-04):代码题在学生端外壳里做,图形化题整屏编辑器(窄屏只给说明)
    {
      path: '/qbank/code/:qid',
      name: 'code-practice',
      component: () => import('./pages/qbank/CodePractice.vue'),
      meta: { student: true, h5: true },
    },
    // 创作室(11-04):随时打开编辑器做作品;列表手机上能看,编辑器页窄屏只给「请用电脑」(页面自己处理)
    {
      path: '/create',
      name: 'create',
      component: () => import('./pages/create/CreateHome.vue'),
      meta: { student: true, h5: true },
    },
    {
      path: '/create/:id',
      name: 'create-editor',
      component: () => import('./pages/create/CreateEditor.vue'),
      meta: { student: true },
    },
    // 阶段测评不上手机(基线 3):手机上这一页只给说明与已出的报告,作答请用电脑(页面自己处理)
    {
      path: '/parts/:partId/assessment',
      name: 'assessment',
      component: () => import('./pages/Assessment.vue'),
      meta: { student: true, h5: true },
    },
    {
      path: '/growth',
      name: 'growth',
      component: () => import('./pages/Growth.vue'),
      meta: { student: true, h5: true },
    },
    {
      path: '/qbank',
      name: 'qbank',
      component: () => import('./pages/QuestionBank.vue'),
      meta: { student: true, h5: true },
    },
    // 课表(排课二期):下一次课、接下来的课、到课记录、申请调课 / 请假;扫老师的二维码签到
    {
      path: '/timetable',
      name: 'timetable',
      component: () => import('./pages/Timetable.vue'),
      meta: { student: true, h5: true },
    },
    // 扫码签到本来就是手机页(自己管 body.m-free 与样式),不挂学生端 H5 的手机版式,原样不动
    {
      path: '/checkin/:code?',
      name: 'lesson-checkin',
      component: () => import('./pages/LessonCheckin.vue'),
      meta: { student: true },
    },
    // 外部官方考代报名(二期 · 原型 student/ext-exam):能代报名的场次、我的代报名、准考信息与官方成绩;?reg= 直达一条报名
    {
      path: '/ext-exam',
      name: 'ext-exam',
      component: () => import('./pages/ExtExam.vue'),
      meta: { student: true, h5: true },
    },
    // 通用页(设计图 21–22)
    {
      path: '/messages',
      name: 'messages',
      component: () => import('./pages/Messages.vue'),
      meta: { student: true, h5: true },
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('./pages/Settings.vue'),
      meta: { student: true, h5: true },
    },
    // 学生端 H5 ·「我的」(手机底部第四个页签):题库、考试、课表、消息、设置、切换学员、退出都收在这里;
    // 电脑上这些入口在顶栏和头像菜单里,直接敲这个地址会回个人设置
    {
      path: '/me',
      name: 'me',
      component: () => import('./pages/Me.vue'),
      meta: { student: true, h5: true },
    },
    // 学生端 H5 ·「请用电脑」说明(手机进考试检录 / 答题时落到这里;免登录)
    {
      path: '/use-desktop',
      name: 'use-desktop',
      component: () => import('./pages/UseDesktop.vue'),
      meta: { public: true, h5: true },
    },
    // 机构导入、还没完成监护人授权的学员:首次授权引导(档案监护人短信 + 三项授权;14 岁以上本人确认)
    {
      path: '/consent-setup/:id',
      name: 'consent-setup',
      component: () => import('./pages/ConsentSetup.vue'),
      meta: { h5: true },
    },
    // 家长端(六页)都有手机版式(二期收尾 · 第二轮):家长几乎都用手机 —— 短信里的链接、周报月报、缴费。
    // 外壳是学生端外壳的家长模式(顶栏当前孩子 + 底部 首页 / 报告 / 订单 / 消息),规则在 ui/parent-h5.ts。
    { path: '/parent', name: 'parent', component: () => import('./pages/Parent.vue'), meta: { h5: true } },
    // 家长端 · 通知与设置(原型 parent/notify)· 学习报告(parent/report ①:周报 / 月报)
    {
      path: '/parent/notify',
      name: 'parent-notify',
      component: () => import('./pages/ParentNotify.vue'),
      meta: { h5: true },
    },
    {
      path: '/parent/report/:studentId?/:kind?/:at?',
      name: 'parent-report',
      component: () => import('./pages/ParentReport.vue'),
      meta: { h5: true },
    },
    // 家长 / 本人购买(第 5 步):课程包 · 我的订单(线下确认、退费)
    {
      path: '/parent/shop',
      name: 'parent-shop',
      component: () => import('./pages/shop/Shop.vue'),
      meta: { h5: true },
    },
    {
      path: '/parent/orders',
      name: 'parent-orders',
      component: () => import('./pages/shop/Orders.vue'),
      meta: { h5: true },
    },
    // 家长端 · 预约与优惠券(招生运营二期:我的预约、活动、优惠券、邀请朋友;学生端不出现)
    {
      path: '/parent/enroll',
      name: 'parent-enroll',
      component: () => import('./pages/ParentEnroll.vue'),
      meta: { h5: true },
    },
    // 第三视角 · 手机端(扫电脑上的二维码进来;不登录账号,凭短码 + 核验后的手机令牌)
    {
      path: '/tv/:code',
      name: 'third-view-phone',
      component: () => import('./pages/ThirdViewPhone.vue'),
      meta: { public: true },
    },
    // 推广链接落地:记下链接码、切到合作机构站点、按落地页跳转
    {
      path: '/r/:code',
      name: 'promo',
      component: () => import('./pages/shop/PromoLanding.vue'),
      meta: { public: true },
    },
    // 大赛官网 → 考试平台的免登录交接
    { path: '/sso', name: 'sso', component: () => import('./pages/Sso.vue'), meta: { public: true } },
    // 大赛官网 · 线下赛参赛码 + 扫现场码自助签到(手机页,不套官网外壳;现场码地址带 ?k=)
    {
      path: '/c/:site/offline/:stageId',
      name: 'portal-offline',
      component: () => import('./pages/portal/PortalOffline.vue'),
      meta: { public: true },
    },
    // 大赛官网(独立站点,自己的会话;设计图 17–20)
    {
      path: '/c/:site',
      component: () => import('./pages/portal/PortalLayout.vue'),
      meta: { public: true },
      children: [
        { path: '', name: 'portal-home', component: () => import('./pages/portal/PortalHome.vue') },
        { path: 'p/:key', name: 'portal-page', component: () => import('./pages/portal/PortalPage.vue') },
        { path: 'news', name: 'portal-news', component: () => import('./pages/portal/PortalNews.vue') },
        {
          path: 'news/:id',
          name: 'portal-notice',
          component: () => import('./pages/portal/PortalNotice.vue'),
        },
        { path: 'signup', name: 'portal-signup', component: () => import('./pages/portal/PortalSignup.vue') },
        {
          path: 'results',
          name: 'portal-results',
          component: () => import('./pages/portal/PortalResults.vue'),
        },
        { path: 'verify', name: 'portal-verify', component: () => import('./pages/portal/PortalVerify.vue') },
        { path: 'login', name: 'portal-login', component: () => import('./pages/portal/PortalLogin.vue') },
        { path: 'me', name: 'portal-me', component: () => import('./pages/portal/PortalMe.vue') },
        {
          path: 'work/:entryId',
          name: 'portal-work',
          component: () => import('./pages/portal/PortalWork.vue'),
        },
        {
          path: 'teacher',
          name: 'portal-teacher',
          component: () => import('./pages/portal/PortalTeacher.vue'),
        },
        { path: 'cert/:id', name: 'portal-cert', component: () => import('./pages/portal/PortalCert.vue') },
      ],
    },
    // 评审员工作台
    {
      path: '/review',
      name: 'review',
      component: () => import('./pages/review/ReviewTasks.vue'),
      meta: { reviewer: true },
    },
    // 课程作品评审(二期职教「企业实战评审」):机构在课程上指定的评审人按评分表评作品;
    // 评审员和被指定的教师都从站内信点进来,所以不限角色(接口按账号卡范围,没被指定的列表为空)
    {
      path: '/review/works',
      name: 'admin-work-review',
      component: () => import('./pages/review/WorkReview.vue'),
    },
    {
      path: '/review/tasks/:id',
      name: 'review-task',
      component: () => import('./pages/review/ReviewTask.vue'),
      meta: { reviewer: true },
    },
    // 评审员 / 裁判的消息(员工身份;外壳铃铛点进来)
    {
      path: '/review/messages',
      name: 'review-messages',
      component: () => import('./pages/review/ReviewMessages.vue'),
      meta: { reviewer: true },
    },
    {
      path: '/judge/messages',
      name: 'judge-messages',
      component: () => import('./pages/judge/JudgeMessages.vue'),
      meta: { judge: true },
    },
    // 线下赛 · 裁判移动端(原型 admin/judge-flow · judge-mobile):扫组二维码 → 登录 → 进组 → 打分 / 选手签字
    {
      path: '/j/:code',
      name: 'judge-join',
      component: () => import('./pages/judge/JudgeJoin.vue'),
      meta: { public: true },
    },
    {
      path: '/judge',
      name: 'judge',
      component: () => import('./pages/judge/JudgeHome.vue'),
      meta: { judge: true },
    },
    {
      path: '/judge/g/:groupId',
      name: 'judge-group',
      component: () => import('./pages/judge/JudgeGroup.vue'),
      meta: { judge: true },
    },
    // 考试端(A18 列表 → A10 检录 → A11 答题 → A12 成绩)
    // 学生端 H5:列表与成绩在手机上能看;检录与答题不上手机(要摄像头、屏幕共享,手机留着当第三视角)
    {
      path: '/exam',
      name: 'exam-list',
      component: () => import('./pages/ExamList.vue'),
      meta: { student: true, h5: true },
    },
    {
      path: '/exam/records/:id/checkin',
      name: 'exam-checkin',
      component: () => import('./pages/ExamCheckin.vue'),
      meta: { student: true, h5: 'pc' },
    },
    {
      path: '/exam/records/:id',
      name: 'exam',
      component: () => import('./pages/Exam.vue'),
      meta: { student: true, h5: 'pc' },
    },
    {
      path: '/exam/records/:id/result',
      name: 'exam-result',
      component: () => import('./pages/ExamResult.vue'),
      meta: { student: true, h5: true },
    },
    // PC 考试客户端(二期 · 原型 student/pc-client):「请用考试客户端」—— 要求客户端的考场,在普通浏览器里点进
    // 检录 / 答题时落到这里(下载、打开客户端、申请改用浏览器、已开考的直接交卷);考试页面本身不加载
    {
      path: '/exam/records/:id/use-client',
      name: 'exam-use-client',
      component: () => import('./pages/ExamUseClient.vue'),
      meta: { student: true, h5: true },
    },
    // 考试客户端下载页(免登录:机构统一装机时不用登学生号)
    {
      path: '/exam-client',
      name: 'exam-client-download',
      component: () => import('./pages/ExamUseClient.vue'),
      meta: { public: true, h5: true },
    },
    // 主办方后台 · 赛事(C1–C22)
    {
      path: '/admin/contests',
      name: 'admin-contests',
      component: () => import('./pages/admin/contest/AdminContests.vue'),
      meta: { staff: true },
    },
    {
      path: '/admin/contests/new',
      name: 'admin-contest-new',
      component: () => import('./pages/admin/contest/AdminContestNew.vue'),
      meta: { staff: true },
    },
    {
      path: '/admin/contests/:id',
      component: () => import('./pages/admin/contest/AdminContestLayout.vue'),
      meta: { staff: true },
      children: [
        {
          path: '',
          name: 'admin-contest',
          component: () => import('./pages/admin/contest/AdminContestOverview.vue'),
        },
        {
          path: 'items',
          name: 'admin-contest-items',
          component: () => import('./pages/admin/contest/AdminContestItems.vue'),
        },
        {
          path: 'registrations',
          name: 'admin-contest-regs',
          component: () => import('./pages/admin/contest/AdminContestRegs.vue'),
        },
        {
          path: 'review',
          name: 'admin-contest-review',
          component: () => import('./pages/admin/contest/AdminContestReview.vue'),
        },
        {
          path: 'offline',
          name: 'admin-contest-offline',
          component: () => import('./pages/admin/contest/AdminContestOffline.vue'),
        },
        {
          path: 'board',
          name: 'admin-contest-board',
          component: () => import('./pages/admin/contest/AdminContestBoard.vue'),
        },
        {
          path: 'notices',
          name: 'admin-contest-notices',
          component: () => import('./pages/admin/contest/AdminContestNotices.vue'),
        },
        {
          path: 'invites',
          name: 'admin-contest-invites',
          component: () => import('./pages/admin/contest/AdminContestInvites.vue'),
        },
      ],
    },
    // 机构后台 · 工作台(原型 admin/dashboard):机构管理员 / 教师每天打开的第一页
    {
      path: '/admin/dashboard',
      name: 'admin-dashboard',
      component: () => import('./pages/admin/org/OrgDashboard.vue'),
      meta: { staff: true },
    },
    // 机构后台 · 学员与教学:学员与班级(学员 / 班级 / 课消)· 班级详情 · 批改与预警
    {
      path: '/admin/org',
      name: 'admin-org',
      component: () => import('./pages/admin/org/OrgStudents.vue'),
      meta: { staff: true },
    },
    {
      path: '/admin/org/classes/:id',
      name: 'admin-org-class',
      component: () => import('./pages/admin/org/OrgClass.vue'),
      meta: { staff: true },
    },
    {
      path: '/admin/teaching',
      name: 'admin-teaching',
      component: () => import('./pages/admin/org/Teaching.vue'),
      meta: { staff: true },
    },
    // 排课管理(二期 · 原型 admin/schedule):周课表 · 排课规则 · 调课与补课 · 与课消联动;归「教务管理」模块
    {
      path: '/admin/schedule',
      name: 'admin-schedule',
      component: () => import('./pages/admin/org/OrgSchedule.vue'),
      meta: { staff: true },
    },
    // 平台 · 合作咨询(官网「加盟合作」留资,2026-11-05)
    {
      path: '/admin/platform/partners',
      name: 'admin-plat-partners',
      component: () => import('./pages/admin/platform/PlatformPartners.vue'),
      meta: { staff: true },
    },
    // 平台 · 节假日日历(排课硬规则「节假日不排课」里平台维护的那一份)
    {
      path: '/admin/platform/holidays',
      name: 'admin-plat-holidays',
      component: () => import('./pages/admin/platform/PlatformHolidays.vue'),
      meta: { staff: true },
    },
    // 课程与关卡(原型 admin/levels):标准库只读 + 自有课程 · 关卡与前置 · 批量导入 · 按班覆盖 · 产品线可见性
    {
      path: '/admin/courses',
      name: 'admin-courses',
      component: () => import('./pages/admin/org/OrgCourses.vue'),
      meta: { staff: true },
    },
    // 教师与账号(原型 admin/staff):账号 · 角色与权限 · 班级授权 · 跨班申请 · 操作留痕
    {
      path: '/admin/staff',
      name: 'admin-staff',
      component: () => import('./pages/admin/org/OrgStaff.vue'),
      meta: { staff: true },
    },
    // 题库与试卷(原型 admin/questions · papers · knowledge):题库(题目 / 隔离池 / 标程自检 / 待审核)· 试卷 · 知识图谱
    {
      path: '/admin/bank',
      name: 'admin-bank',
      component: () => import('./pages/admin/bank/BankPage.vue'),
      meta: { staff: true },
    },
    {
      path: '/admin/papers',
      name: 'admin-papers',
      component: () => import('./pages/admin/bank/PapersPage.vue'),
      meta: { staff: true },
    },
    {
      path: '/admin/knowledge',
      name: 'admin-knowledge',
      component: () => import('./pages/admin/bank/KnowledgePage.vue'),
      meta: { staff: true },
    },
    // 平台后台 · 机构与租户(原型 platform/tenants):列表与用量 · 套餐与配额 · 新建 / 续费 / 提额 / 重置管理员密码
    {
      path: '/admin/platform/tenants',
      name: 'admin-plat-tenants',
      component: () => import('./pages/admin/platform/PlatformTenants.vue'),
      meta: { staff: true },
    },
    // 首次开通引导(原型 global/onboarding):品牌 → 教师 → 班级与学员 → 课程 → 检查清单
    {
      path: '/admin/onboarding',
      name: 'admin-onboarding',
      component: () => import('./pages/admin/org/OrgOnboarding.vue'),
      meta: { staff: true },
    },
    // 用户与权限(原型 platform/users):角色矩阵 · 刻意看不到的 · 账号与合并 · 平台账号
    {
      path: '/admin/platform/users',
      name: 'admin-plat-users',
      component: () => import('./pages/admin/platform/PlatformUsers.vue'),
      meta: { staff: true },
    },
    // 平台 · 考试审批与解锁(S-1 归档解锁 · B15② 延长本场超 60 分钟审批):平台管理员
    {
      path: '/admin/platform/exams',
      name: 'admin-plat-exams',
      component: () => import('./pages/admin/platform/PlatformExams.vue'),
      meta: { staff: true },
    },
    // 审计日志(原型 platform/audit):平台看全部租户;机构只看本租户(同一页面,scope 区分)
    {
      path: '/admin/platform/audit',
      name: 'admin-plat-audit',
      component: () => import('./pages/admin/platform/PlatformAudit.vue'),
      props: { scope: 'platform' },
      meta: { staff: true },
    },
    {
      path: '/admin/audit',
      name: 'admin-audit',
      component: () => import('./pages/admin/platform/PlatformAudit.vue'),
      props: { scope: 'tenant' },
      meta: { staff: true },
    },
    // 下载中心(原型 global/downloads):异步导出任务 · 7 天有效 · 水印与脱敏 · 下载留痕
    {
      path: '/admin/downloads',
      name: 'admin-downloads',
      component: () => import('./pages/admin/Downloads.vue'),
      meta: { staff: true },
    },
    // 通知与公告(原型 admin/notices):公告 · 系统通知文案 · 发送记录与触达 · 规则
    {
      path: '/admin/notices',
      name: 'admin-notices',
      component: () => import('./pages/admin/org/OrgNotices.vue'),
      meta: { staff: true },
    },
    // 后台 · 消息中心(员工身份):系统通知与发给教师的机构公告;后台外壳顶栏铃铛点进来
    {
      path: '/admin/messages',
      name: 'admin-messages',
      component: () => import('./pages/admin/AdminMessages.vue'),
      meta: { staff: true },
    },
    // 平台公告与维护:维护 / 政策公告(各端顶部与消息中心显示)+ 维护开关
    {
      path: '/admin/platform/notices',
      name: 'admin-plat-notices',
      component: () => import('./pages/admin/platform/PlatformNotices.vue'),
      meta: { staff: true },
    },
    // 外部服务(外部件接入):对象存储 / 短信 / 人脸比对 / 支付 / 大模型的状态与连通测试
    {
      path: '/admin/platform/external',
      name: 'admin-plat-external',
      component: () => import('./pages/admin/platform/PlatformExternal.vue'),
      meta: { staff: true },
    },
    // 平台 · 赛事归档解锁(闭环 §6:平台管理员,理由留痕,解锁后重新公示)
    {
      path: '/admin/platform/contests',
      name: 'admin-plat-contests',
      component: () => import('./pages/admin/contest/PlatformContests.vue'),
      meta: { staff: true },
    },
    // 规则配置中心(原型 platform/settings):平台默认值;机构在「机构规则」里按范围覆盖
    // 平台 · 考试客户端(二期):PC 考试客户端的版本、构建密钥、下载地址、最低可用版本
    {
      path: '/admin/platform/exam-client',
      name: 'admin-plat-exam-client',
      component: () => import('./pages/admin/platform/PlatformExamClient.vue'),
      meta: { staff: true },
    },
    // 平台 · 私有化部署(二期):客户台账、授权签发 / 续期 / 增购 / 吊销、版本发布与交付、机构迁移单
    {
      path: '/admin/platform/private-deploy',
      name: 'admin-plat-private-deploy',
      component: () => import('./pages/admin/platform/PlatformPrivateDeploy.vue'),
      meta: { staff: true },
    },
    // 私有化实例 · 授权与升级(系统管理员):机器码、当前授权与用量、导入授权、在线回报、版本与升级记录
    {
      path: '/admin/platform/instance',
      name: 'admin-plat-instance',
      component: () => import('./pages/admin/platform/PlatformInstance.vue'),
      meta: { staff: true },
    },
    {
      path: '/admin/platform/rules',
      name: 'admin-plat-rules',
      component: () => import('./pages/admin/platform/PlatformRules.vue'),
      meta: { staff: true },
    },
    {
      path: '/admin/org/rules',
      name: 'admin-org-rules',
      component: () => import('./pages/admin/org/OrgRules.vue'),
      meta: { staff: true },
    },
    // 数据统计(原型 platform/stats):平台看全平台 / 按租户;机构「数据看板」是同一口径的本租户切片
    {
      path: '/admin/platform/stats',
      name: 'admin-plat-stats',
      component: () => import('./pages/admin/platform/PlatformStats.vue'),
      props: { scope: 'platform' },
      meta: { staff: true },
    },
    {
      path: '/admin/stats',
      name: 'admin-org-stats',
      component: () => import('./pages/admin/platform/PlatformStats.vue'),
      props: { scope: 'tenant' },
      meta: { staff: true },
    },
    // 合作机构后台(第 5 步):概览 / 推广链接 / 学员归属 / 分成与对账 / 下级机构
    {
      path: '/admin/channel',
      name: 'admin-channel',
      component: () => import('./pages/admin/channel/ChannelOverview.vue'),
      meta: { staff: true },
    },
    {
      path: '/admin/channel/links',
      name: 'admin-channel-links',
      component: () => import('./pages/admin/channel/ChannelLinks.vue'),
      meta: { staff: true },
    },
    // 公立学校模式(原型 channel/school):免费专区 · 付费提醒 · 班级与批量激活 · 分成确认
    {
      path: '/admin/school',
      name: 'admin-school',
      component: () => import('./pages/admin/school/SchoolMode.vue'),
      meta: { staff: true },
    },
    // 招生运营(原型 admin/marketing):营销物料 · 体验课(培训机构标配)
    {
      path: '/admin/marketing',
      name: 'admin-marketing',
      component: () => import('./pages/admin/org/OrgMarketing.vue'),
      meta: { staff: true },
    },
    // 招生运营二期(原型 admin/enrollment;「招生运营」采购模块):预约试听 · 活动报名 · 优惠券 · 招生漏斗
    {
      path: '/admin/enrollment',
      name: 'admin-enrollment',
      component: () => import('./pages/admin/org/OrgEnrollment.vue'),
      meta: { staff: true },
    },
    // 品牌与模块(原型 platform/modules):机构看本机构的模块、增购 / 在线自助开通、品牌与白标
    {
      path: '/admin/modules',
      name: 'admin-modules',
      component: () => import('./pages/admin/org/OrgModules.vue'),
      meta: { staff: true },
    },
    // 续费与扩容(10-02 口径 5 / 6):服务状态与用量 · 套餐在线续费 · 学员扩容包 · 临时提额申请;冻结时是后台的整页入口
    {
      path: '/admin/renew',
      name: 'admin-renew',
      component: () => import('./pages/admin/org/OrgRenew.vue'),
      meta: { staff: true },
    },
    // AI 算力(用户故事 D4-2):机构额度与买算力包、本月用量、按学员的用量明细
    {
      path: '/admin/ai-quota',
      name: 'admin-ai-quota',
      component: () => import('./pages/admin/org/OrgAiQuota.vue'),
      meta: { staff: true },
    },
    // 平台 · 模块与采购:任一租户的模块开通 / 停用、增购预检、品牌审核队列
    {
      path: '/admin/platform/modules',
      name: 'admin-plat-modules',
      component: () => import('./pages/admin/platform/PlatformModules.vue'),
      meta: { staff: true },
    },
    // 平台 · 标准课程库与内容生产(原型 platform/content):课程与版本 · 关卡编辑器 · 发布到租户 · 内容审核
    {
      path: '/admin/platform/content',
      name: 'admin-plat-content',
      component: () => import('./pages/admin/platform/PlatformContent.vue'),
      meta: { staff: true },
    },
    // 平台 · 外部考试目录(4.4.3 · 基线 6):考纲映射(平台知识点)· 报名窗口 · 官网 · 推荐时机
    {
      path: '/admin/platform/external-exams',
      name: 'admin-plat-ext-exams',
      component: () => import('./pages/admin/platform/PlatformExternalExams.vue'),
      meta: { staff: true },
    },
    // 机构 · 认证考报名(A9-4):开放的认证考 · 本机构学员资格 / 放宽 / 报名情况
    {
      path: '/admin/cert-exams',
      name: 'admin-cert-exams',
      component: () => import('./pages/admin/org/OrgCertExams.vue'),
      meta: { staff: true },
    },
    // 机构 · 外部考代报名(二期;归「在线考试系统」模块):代报名资质 · 场次 · 名单审核 · 报送官方 · 准考信息与成绩导入 · 退费改期
    {
      path: '/admin/ext-exam',
      name: 'admin-ext-exam',
      component: () => import('./pages/admin/org/OrgExtExam.vue'),
      meta: { staff: true },
    },
    // 平台 · 数字人中心(原型 platform/digital-human):形象库 · 话术库 · 关卡素材 · 审核队列 · 答疑边界
    {
      path: '/admin/platform/dh',
      name: 'admin-plat-dh',
      component: () => import('./pages/admin/platform/PlatformDh.vue'),
      meta: { staff: true },
    },
    // 平台 · 数字人增强(二期 · 原型 platform/digital-human-plus):3D 形象与表情 · 方言与多语 · 内容生成 · 授课辅助升级
    {
      path: '/admin/platform/dh-plus',
      name: 'admin-plat-dh-plus',
      component: () => import('./pages/admin/platform/PlatformDhPlus.vue'),
      meta: { staff: true },
    },
    // 教师课堂演示:投屏讲解 + 逐题演示(答案与解析只在这里给老师看)
    {
      path: '/admin/dh/demo/:levelId',
      name: 'admin-dh-demo',
      component: () => import('./pages/admin/org/DhDemo.vue'),
      meta: { staff: true },
    },
    // 四位一体家长说明页(H5,免登录;物料里的「家长说明页」)
    {
      path: '/intro/:code',
      name: 'parent-intro',
      component: () => import('./pages/public/ParentIntro.vue'),
      meta: { public: true },
    },
    // 招生运营二期:预约试听页 / 活动报名页(H5,免登录;链接带 ?src= / ?ref= / ?inv= 记来源、归属、老带新)
    {
      path: '/book/:code',
      name: 'enroll-book',
      component: () => import('./pages/public/EnrollBooking.vue'),
      meta: { public: true },
    },
    {
      path: '/act/:code/:id',
      name: 'enroll-activity',
      component: () => import('./pages/public/EnrollActivity.vue'),
      meta: { public: true },
    },
    // 激活码(原型 channel/codes):采购 · 库存 · 转赠 · 批量激活 · 记录(合作机构与开通了模块的机构)
    {
      path: '/admin/channel/codes',
      name: 'admin-channel-codes',
      component: () => import('./pages/admin/channel/ChannelCodes.vue'),
      meta: { staff: true },
    },
    {
      path: '/admin/platform/activation',
      name: 'admin-plat-activation',
      component: () => import('./pages/admin/platform/PlatformActivation.vue'),
      meta: { staff: true },
    },
    {
      path: '/admin/channel/members',
      name: 'admin-channel-members',
      component: () => import('./pages/admin/channel/ChannelMembers.vue'),
      meta: { staff: true },
    },
    {
      path: '/admin/channel/finance',
      name: 'admin-channel-finance',
      component: () => import('./pages/admin/channel/ChannelFinance.vue'),
      meta: { staff: true },
    },
    {
      path: '/admin/channel/subs',
      name: 'admin-channel-subs',
      component: () => import('./pages/admin/channel/ChannelSubs.vue'),
      // 只有一级合作机构有下级(层级上限 2):二级直接敲地址进来去无权限页
      meta: { staff: true, channelL1: true },
    },
    // 卖方后台:课程包 / 订单(含线下收款登记)/ 退费审核
    {
      path: '/admin/packs',
      name: 'admin-packs',
      component: () => import('./pages/admin/commerce/SellerPacks.vue'),
      meta: { staff: true },
    },
    {
      path: '/admin/orders',
      name: 'admin-orders',
      component: () => import('./pages/admin/commerce/SellerOrders.vue'),
      meta: { staff: true },
    },
    {
      path: '/admin/refunds',
      name: 'admin-refunds',
      component: () => import('./pages/admin/commerce/SellerRefunds.vue'),
      meta: { staff: true },
    },
    // 平台 · 渠道与结算
    {
      path: '/admin/platform/channels',
      name: 'admin-plat-channels',
      component: () => import('./pages/admin/platform/PlatformChannels.vue'),
      meta: { staff: true },
    },
    {
      path: '/admin/platform/finance',
      name: 'admin-plat-finance',
      component: () => import('./pages/admin/platform/PlatformFinance.vue'),
      meta: { staff: true },
    },
    // 机构后台 · 考务(B7 建考 / B15 考场与选手 / B8 监考 / B9 成绩);meta.staff 需要员工角色
    {
      // 用 beforeEnter 而不是 redirect:redirect 在全局守卫加载身份之前就算了,刷新页面时角色还是空的
      path: '/admin',
      name: 'admin-home',
      component: { render: () => null },
      meta: { staff: true },
      beforeEnter: async () => {
        const s = useSession();
        // 平台站点的运营先到机构与租户;机构管理员 / 教师每天第一页是工作台
        if (s.canPlatformOps) {
          // 私有化实例的系统管理员第一页是「授权与升级」(没激活时别的页面都是空的)
          const t = useTenantStatus();
          await t.load();
          return { name: t.isPrivate && s.isPlatformAdmin ? 'admin-plat-instance' : 'admin-plat-tenants' };
        }
        // 机构冻结 / 待删除:管理员第一页就是续费(其余员工的整页在全局守卫里给)
        if (!s.isPlatformSite && s.canModules) {
          const t = useTenantStatus();
          await t.load();
          if (t.suspended) return { name: 'admin-renew' };
        }
        // 新开的机构:管理员第一次进后台先走开通引导(跳过或完成后不再来)
        if (s.isOrgAdmin && !s.isPlatformSite) {
          const t = useTenantStatus();
          await t.load();
          if (t.info?.onboardingPending) return { name: 'admin-onboarding' };
        }
        if (s.canTeach && !s.isPlatformSite) return { name: 'admin-dashboard' };
        if (s.canExam) return { name: 'admin-exams' };
        if (s.isChannel) return { name: 'admin-channel' };
        if (s.canContest) return { name: 'admin-contests' };
        if (s.canPlatformFinance) return { name: 'admin-plat-finance' };
        if (s.canSell) return { name: 'admin-orders' };
        if (s.canBank) return { name: 'admin-bank' };
        if (s.canPlatformAudit) return { name: 'admin-plat-audit' };
        return { name: 'admin-exams' };
      },
    },
    {
      path: '/admin/exams',
      name: 'admin-exams',
      component: () => import('./pages/admin/AdminExams.vue'),
      meta: { staff: true },
    },
    {
      path: '/admin/exams/new',
      name: 'admin-exam-new',
      component: () => import('./pages/admin/AdminExamForm.vue'),
      meta: { staff: true },
    },
    // 第三视角监考(原型 admin/third-view):考场开关与参数 · 选手与二维码 · 实时异常 · 记录与导出;?exam=&room=&tab=
    {
      path: '/admin/third-view',
      name: 'admin-third-view',
      component: () => import('./pages/admin/AdminThirdView.vue'),
      meta: { staff: true },
    },
    // 补考(原型 admin/makeup):成绩补考 / 故障重考 · 学生申请审批 · 历史;?exam= 预选原考试
    {
      path: '/admin/makeup',
      name: 'admin-makeup',
      component: () => import('./pages/admin/AdminMakeup.vue'),
      meta: { staff: true },
    },
    {
      path: '/admin/exams/:id/edit',
      name: 'admin-exam-edit',
      component: () => import('./pages/admin/AdminExamForm.vue'),
      meta: { staff: true },
    },
    {
      path: '/admin/exams/:id',
      component: () => import('./pages/admin/AdminExamLayout.vue'),
      meta: { staff: true },
      children: [
        { path: '', name: 'admin-exam', component: () => import('./pages/admin/AdminExamOverview.vue') },
        {
          path: 'rooms',
          name: 'admin-exam-rooms',
          component: () => import('./pages/admin/AdminExamRooms.vue'),
        },
        {
          path: 'records',
          name: 'admin-exam-records',
          component: () => import('./pages/admin/AdminExamRecords.vue'),
        },
        // 认证考:自主报名名单与缴费退费 · 认证证书(用户故事 A9)
        {
          path: 'registrations',
          name: 'admin-exam-registrations',
          component: () => import('./pages/admin/AdminExamRegistrations.vue'),
        },
        {
          path: 'proctor',
          name: 'admin-exam-proctor',
          component: () => import('./pages/admin/AdminExamProctor.vue'),
        },
        // ACM 赛制:实时榜单(真实榜 / 封榜视图)、揭榜、滚榜
        {
          path: 'board',
          name: 'admin-exam-board',
          component: () => import('./pages/admin/AdminExamBoard.vue'),
        },
        {
          path: 'results',
          name: 'admin-exam-results',
          component: () => import('./pages/admin/AdminExamResults.vue'),
        },
      ],
    },
    // 错误与状态页(设计图 24):无权限 / 维护中 / 加载失败 / 404(兜底,必须放最后)
    {
      path: '/forbidden',
      name: 'forbidden',
      component: StatusPage,
      meta: { public: true, status: 'forbidden', h5: true },
    },
    {
      path: '/maintenance',
      name: 'maintenance',
      component: StatusPage,
      meta: { public: true, status: 'maintenance', h5: true },
    },
    {
      path: '/load-failed',
      name: 'load-failed',
      component: StatusPage,
      meta: { public: true, status: 'failed', h5: true },
    },
    // 机构冻结 / 待删除时,非管理员员工进后台看到的整页(管理员去「续费与扩容」)
    {
      path: '/suspended',
      name: 'suspended',
      component: StatusPage,
      meta: { public: true, status: 'suspended' },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: StatusPage,
      meta: { public: true, status: 'lost', h5: true },
    },
  ],
});

/** 发版后旧页面拉不到新的分包(文件名带哈希已换):去「加载失败」页,重试时整页重新加载拿新版本 */
router.onError((err: unknown, to) => {
  const msg = err instanceof Error ? err.message : String(err);
  if (/dynamically imported module|Importing a module script failed|Failed to fetch/i.test(msg))
    void router.replace({ name: 'load-failed', query: { to: to.fullPath } });
});

/** 按模块配置品牌时,考试端 / 大赛门户 / 学习平台各用自己的主题色;后台用统一品牌 */
router.afterEach((to) => {
  useTenantStatus().applySurface(surfaceOf(to.path));
  // 学生端 H5:告诉视口 store 这个路由有没有手机版式(手机上才给 <html> 加 class「h5」)
  useViewport().onRoute(to.meta['h5']);
});

/**
 * 站点品牌先于首屏:页面的分包下好了、真要画之前,先等站点信息(只有第一次进页面真的在等,平时一两百毫秒,
 * 最多 TENANT_WAIT_MS;等的时候页面上是个不带品牌的小转圈,见 App.vue)。品牌和主题色就位了再出页面 ——
 * 机构站点(尤其开了白标的)不能先闪一下平台的名字、图标和蓝色再换成自己的。
 * 放在 beforeResolve 而不是 beforeEach:站点信息的请求在 beforeEach 里就发出去了,和页面分包的下载同时进行,不是排着队等。
 */
router.beforeResolve(() => useTenantStatus().ready(TENANT_WAIT_MS));

router.beforeEach(async (to) => {
  const session = useSession();
  // 学生端 H5:地址带 ?h5=1 / 0 / auto 手动指定版式(调试 / 截图);
  // 手机进只能在电脑上用的页面(考试检录 / 答题)→「请用电脑」说明页,页面本身不加载
  const viewport = useViewport();
  viewport.applyQuery(to.query['h5']);
  if (viewport.phone && to.meta['h5'] === 'pc')
    return {
      name: 'use-desktop',
      query: { what: desktopOnlyWhatOf(to.name), from: to.fullPath },
      replace: true,
    };
  // 「我的」是手机页签;电脑上这些入口在顶栏与头像菜单里,敲地址进来的回个人设置
  if (to.name === 'me' && !viewport.phone) return { name: 'settings', replace: true };
  // 招生物料 / 家长说明页上的机构链接带 ?site=站点码(没有自有域名时):公开页或还没登录时切到那个机构站点
  const site = to.query['site'];
  if (
    typeof site === 'string' &&
    /^[a-z][a-z0-9]{3,11}$/.test(site) &&
    site !== session.tenantCode &&
    (to.meta['public'] || !session.accessToken)
  )
    session.setTenantOverride(site);
  // 机构自有域名(A18-5):先让服务端按域名认出站点,站点码随 /v1/site/tenant 带回,后面的品牌 / 菜单 / 权限才按机构站点走
  if (!session.tenantCode) await useTenantStatus().load();
  // 站点码已经知道(子域名 / 手动选的站点):站点信息先发出去、这里不等 —— 页面的分包同时在下,
  // 到真要画页面之前(下面的 beforeResolve)再等它
  else void useTenantStatus().load();
  if (to.meta['public']) return true;
  if (!session.accessToken) return { name: 'login', query: { redirect: to.fullPath } };
  if (!session.me) {
    try {
      await session.loadMe();
    } catch {
      await session.logout();
      return { name: 'login', query: { redirect: to.fullPath } };
    }
  }
  // 无权限页写明需要的角色与当前角色(设计图 24),不再悄悄跳回首页
  if (to.matched.some((m) => m.meta['staff']) && !session.isStaff)
    return { name: 'forbidden', query: { need: 'staff', from: to.fullPath } };
  // 机构冻结 / 待删除(10-02 口径 5):后台只留续费、下载中心、审计与考中考后的入口;
  // 管理员其余页面去「续费与扩容」整页,别的员工看「机构服务已暂停,请联系管理员」
  if (to.matched.some((m) => m.meta['staff']) && !session.isPlatformSite) {
    const t = useTenantStatus();
    await t.load();
    if (t.suspended && !SUSPENDED_OPEN.test(String(to.name ?? '')))
      return session.canModules
        ? { name: 'admin-renew' }
        : { name: 'suspended', query: { from: to.fullPath } };
  }
  // 再细分的角色:评审员 / 裁判 / 一级合作机构(二级直接敲地址进「下级机构」也给无权限页)
  const need = roleNeedOf(to.meta, {
    isReviewer: session.isReviewer,
    isJudge: session.isJudge,
    isChannelL1: session.isChannelL1,
  });
  if (need) return { name: 'forbidden', query: { need, from: to.fullPath } };
  if (to.meta['student'] && !session.me?.studentId) {
    // 一号多孩:只有一个就自动选中,多个去选
    const list = await session.loadStudents().catch(() => []);
    if (list.length === 1 && list[0]) {
      await session.selectStudent(list[0].id);
      return consentGate(to.fullPath, String(to.name ?? ''));
    }
    return { name: 'select-student', query: { redirect: to.fullPath } };
  }
  if (to.meta['student']) return consentGate(to.fullPath, String(to.name ?? ''));
  return true;
});

/** 冻结 / 待删除时后台还能进的页面:续费、下载中心、审计、采购记录、考务与赛事(已发布 / 已开放的按原计划进行) */
const SUSPENDED_OPEN =
  /^admin-(home|renew|downloads|audit|modules|exams?|exam-|third-view|makeup|contests?|contest-)/;

/**
 * 机构导入、还没完成监护人授权的学员(10-02 口径 12):学习与考试入口先拦住,去首次授权引导;
 * 个人设置与消息照常能看。接口层不拦(导入建档的演示学员与历史数据很多,只在前端按档案授权状态拦)。
 * 课表与扫码签到也不拦(排课二期):那是线下上课的事务,不是在平台上学习,上课时不该被授权引导挡住。
 * 手机上的「我的」页也不拦(学生端 H5):它只是设置、消息、课表这些入口的集合,不是学习入口。
 */
function consentGate(fullPath: string, name: string) {
  const s = useSession().student;
  if (
    s?.consentState === 'pending_verify' &&
    !['settings', 'messages', 'timetable', 'lesson-checkin', 'me'].includes(name)
  )
    return { name: 'consent-setup', params: { id: s.id }, query: { redirect: fullPath } };
  return true;
}
